import { useMemo, useState } from 'react'
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Flex,
  Form,
  Input,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { useAuth } from '@/shared/auth/useAuth'
import { usePermissions } from '@/shared/auth/usePermissions'
import { formatDateTime, utcTooltip } from '@/shared/lib/format'
import { formatMoneyValue } from '@/shared/lib/money'
import {
  applyLocalList,
  byInstant,
  byNumber,
  byText,
  localSortChange,
  sortColumn,
  useLocalListState,
} from '@/shared/lib/localList'
import { EmptyState } from '@/shared/ui/EmptyState'
import { ExpandableHint } from '@/shared/ui/ExpandableHint'
import { FilterDrawer } from '@/shared/ui/FilterDrawer'
import { PageHeader } from '@/shared/ui/PageHeader'
import { RowActions } from '@/shared/ui/table/RowActions'
import type { TableState } from '@/shared/ui/table/tableState'
import {
  StockApprovalDecisionModal,
  type ApprovalDecisionResult,
} from '@/features/inventory/StockApprovalDecisionModal'
import { actorLabel } from '@/features/identity/actorName'
import { useActorDirectory } from '@/features/identity/useActorDirectory'
import { StockOperationAlert } from '@/features/inventory/StockOperationAlert'
import { StockPolicyModal } from '@/features/inventory/StockPolicyModal'
import {
  APPROVAL_STATES,
  approvalBlock,
  approvalRequired,
  fetchStockApprovals,
  fetchStockItems,
  fetchStockPolicy,
  fetchWarehouses,
  isCountedQuantity,
  isOwnApproval,
  type ApprovalAction,
  type ApprovalState,
  type StockApproval,
  type StockPolicy,
} from '@/features/inventory/stockApi'
import type { StockAction } from '@/features/inventory/StockMovementModal'

/**
 * Holat rangi — QAROR OG'IRLIGI bo'yicha, alifbo bo'yicha emas.
 * `PENDING` sariq (aralashuv kerak), tasdiqlangan yashil, rad etilgan
 * qizil, bekor qilingan kulrang (hech kim rad etmagan — so'ragan odam
 * o'zi qaytarib olgan).
 */
const STATE_COLOR: Record<ApprovalState, string> = {
  PENDING: 'gold',
  APPROVED: 'green',
  REJECTED: 'red',
  CANCELLED: 'default',
}

/**
 * Tasdiqlangan so'rovning natijasini `StockOperationAlert` ko'rsatadi va
 * unga PANEL amali kerak (`StockAction`), harakat turi emas.
 *
 * ⚠️ Xarita shu yerda va faqat shu yerda: `ADJUSTMENT` — inventarizatsiya,
 * `DAMAGED`/`LOST` — hisobdan chiqarish. Boshqa tur bu ro'yxatga umuman
 * tushmaydi (`ApprovalState.isApprovable`), lekin `MovementKind` keng
 * turgani uchun zaxira qiymat beriladi.
 */
function actionOfKind(kind: StockApproval['kind']): StockAction {
  if (kind === 'ADJUSTMENT' || kind === 'INTERNAL_USE') return kind
  return 'WRITE_OFF'
}

interface Pending {
  action: ApprovalAction
  approval: StockApproval
}

type Filters = TableState['filters']

/** Filtr qoralamasida qiymat DOIM massiv, tanlagich esa yakka qiymat kutadi */
function draftValue(draft: Filters, key: string): string | null {
  const values = draft[key]
  return values && values.length > 0 ? (values[0] ?? null) : null
}

/**
 * Ikkinchi imzo — kutayotgan so'rovlar va tashkilot chegarasi.
 *
 * <h2>⚠️⚠️ SHU EKRANDA UCHTA HAR XIL HUQUQ UCHRASHADI</h2>
 * Ular BIR-BIRINI ALMASHTIRMAYDI va panel har birini ALOHIDA tekshiradi:
 *
 * | Nima | Huquq | Server |
 * |---|---|---|
 * | Ro'yxat va chegarani KO'RISH | `inventory.stock.read` | `GET /approvals`, `GET /policy` |
 * | Tasdiq / rad / bekor QARORI | `inventory.stock.adjust` | `POST /approvals/{id}/...` |
 * | Chegarani O'ZGARTIRISH | **`settings.update`** | `PUT /policy` |
 *
 * ⚠️ UCHINCHI QATOR — ENG OSON ADASHILADIGAN JOY va u ONGLI qaror
 * (`StockResource#updatePolicy`): chegarani `inventory.stock.adjust`
 * bilan o'zgartirsa bo'lganda, nazorat MAVJUD BO'LMASDI — nazorat
 * qilinayotgan omborchi chegarani 0 ga tushirib, hisobdan chiqarib,
 * keyin qaytarib qo'ya olardi.
 *
 * <h2>⚠️ NEGA MARSHRUT IKKI HUQUQNI QABUL QILADI</h2>
 * O'lchandi (`V202608131600` va `identity.seed_system_roles()`):
 * `inventory.stock.*` faqat `SUPERADMIN` ga (butun katalog) va
 * `inventory.stock.read` — `.read` bilan tugagani uchun — `VIEWER` ga
 * tushadi. `ADMIN` da esa `settings.update` BOR, ombor huquqlari YO'Q.
 * Ya'ni chegarani o'zgartirishi KERAK bo'lgan odam — aynan `ADMIN` —
 * `inventory.stock.read` talab qiladigan sahifani UMUMAN ocholmasdi va
 * menyuda uni ko'rmasdi ham. Sabab esa ekranda emas, huquq seedida
 * qidirilardi.
 *
 * Shuning uchun marshrut `inventory.stock.read` YOKI `settings.update`
 * bilan ochiladi, sahifa esa har blokni O'Z huquqi bo'yicha tekshirib,
 * yetmaganda SABABINI yozadi. Bo'sh ekran ko'rsatilmaydi.
 *
 * <h2>⚠️ SO'ROVLAR RO'YXATI BO'SH BO'LSA — SABABI IKKI XIL</h2>
 * "Hech kim hech narsa so'ramadi" va "chegara 0, ya'ni oqim umuman
 * o'chiq" bir xil ko'rinardi. Ikkinchisi alohida aytiladi, aks holda
 * operator nazorat ishlayapti deb o'ylab yurardi.
 */
export function StockApprovalsPage() {
  const { t } = useTranslation()
  const { user } = useAuth()
  const { can } = usePermissions()

  const canRead = can('inventory.stock.read')
  const canAdjust = can('inventory.stock.adjust')
  const canSetThreshold = can('settings.update')

  // ⚠️ B4: `requested_by`/`decided_by` da UUID turadi. Nom identity
  //    ro'yxatidan keladi; `useActorDirectory` `user.read` bo'lmasa
  //    so'rov YUBORMAYDI va xarita bo'sh qoladi (403 sikli yo'q).
  const actors = useActorDirectory()

  /**
   * ⚠️ `id` VA `username` — ikkalasi ham. Server `requested_by` ga
   * `X-Xsib-Username` bo'lsa username, bo'lmasa `userId` yozadi, gateway
   * esa bu sarlavhani hozircha umuman yubormaydi (`isOwnApproval`
   * javadoc'ida o'lchov).
   */
  const identity = useMemo(
    () => (user ? { id: user.id, username: user.username } : null),
    [user],
  )

  /**
   * ⚠️ FILTRLAR MANZILDA (`useTableUrlState`): «shu omborning kutayotgan
   * so'rovlari» degan havolani hamkasbga yuborish mumkin bo'lsin va
   * sahifa yangilanganda tanlov yo'qolmasin.
   *
   * ⚠️ Standart tartib — `requestedAt descend`, ya'ni AYNAN «oxirgi
   * qo'shilgan birinchi»: bu ro'yxatda so'rovning tug'ilish vaqti
   * `requestedAt` va boshqa yaratilish maydoni yo'q.
   *
   * ⚠️ `pending` — TORAYTIRUVCHI filtr sanalmaydi: u sukut bo'yicha
   * YOQIQ va uni sanash «Tozalash» tugmasini doim ko'rsatardi.
   */
  const list = useLocalListState({
    defaultSort: { field: 'requestedAt', order: 'descend' },
    narrowingKeys: ['node', 'state'],
  })
  const nodeId = list.filter('node')
  /** Sukut bo'yicha YOQIQ — URL'da faqat O'CHIRILGANI saqlanadi */
  const pendingOnly = list.filter('pending') !== 'no'
  const [pending, setPending] = useState<Pending | null>(null)
  const [policyOpen, setPolicyOpen] = useState(false)
  const [done, setDone] = useState<ApprovalDecisionResult | null>(null)
  /**
   * ⚠️ Saqlash natijasi ALOHIDA saqlanadi va kartadagi qiymatdan
   * OLINMAYDI. Sabab aynan shu ekranning tuzog'ida: `settings.update`
   * bori, lekin `inventory.stock.read` yo'q odam uchun `GET /policy`
   * umuman yuborilmaydi — karta eski holida qoladi va saqlash HECH
   * QANDAY iz qoldirmasdi ("bosdim, hech narsa bo'lmadi").
   */
  const [policySaved, setPolicySaved] = useState<StockPolicy | null>(null)

  const warehouses = useQuery({
    queryKey: queryKeys.stock.warehouses,
    queryFn: fetchWarehouses,
    staleTime: 5 * 60_000,
    enabled: canRead,
  })

  const items = useQuery({
    queryKey: queryKeys.stock.items(false),
    queryFn: () => fetchStockItems(false),
    staleTime: 5 * 60_000,
    enabled: canRead,
  })

  const approvals = useQuery({
    queryKey: queryKeys.stock.approvals(nodeId, pendingOnly),
    queryFn: ({ signal }) => fetchStockApprovals(nodeId, pendingOnly, signal),
    enabled: canRead,
  })

  const policy = useQuery({
    queryKey: queryKeys.stock.policy,
    queryFn: ({ signal }) => fetchStockPolicy(signal),
    enabled: canRead,
  })

  const warehouseName = useMemo(() => {
    const map = new Map<string, string>()
    for (const warehouse of warehouses.data ?? []) map.set(warehouse.id, warehouse.displayName)
    return map
  }, [warehouses.data])

  const itemLabel = useMemo(() => {
    const map = new Map<string, { name: string; sku: string; unit: string }>()
    for (const item of items.data ?? []) {
      map.set(item.id, { name: item.name, sku: item.sku, unit: item.unit })
    }
    return map
  }, [items.data])

  /** SERVER bergan qatorlar — sarlavhadagi sanoq SHULARDAN */
  const serverRows = useMemo(() => (approvals.data ? [...approvals.data] : []), [approvals.data])

  /*
    ⚠️ QIDIRUV VA HOLAT FILTRI MIJOZDA: `StockResource#approvals` faqat
    `nodeId` va `pendingOnly` ni oladi — `q`, `state`, `page`, `size`
    YO'Q. Javob esa TO'LIQ ro'yxat, ya'ni saralash butun ro'yxatga
    qo'llanadi va "faqat joriy sahifa saralandi" degan yolg'on tartib
    paydo bo'lolmaydi (`shared/lib/localList.ts`).
  */
  const rows = useMemo(
    () =>
      applyLocalList(serverRows, {
        search: list.search,
        searchFields: (row) => [
          itemLabel.get(row.itemId)?.name,
          itemLabel.get(row.itemId)?.sku,
          row.reason,
          row.assetTag,
          row.docNo,
        ],
        filters: {
          state: (row, value) => row.state === value,
        },
        filterState: list.state.filters,
        comparators: {
          requestedAt: byInstant((row) => row.requestedAt),
          quantity: byNumber((row) => row.quantity),
          // ⚠️ MINOR birlikdagi butun son bo'yicha — bir tenantda
          //    valyuta bitta bo'lgani uchun tartib to'g'ri chiqadi.
          //    Aralash valyutada bu taqqoslash MA'NOSIZ bo'lardi va
          //    o'shanda bu ustunni saralatmaslik kerak.
          cost: byNumber((row) => row.estimatedCost.amount),
          state: byText((row) => row.state),
        },
        sort: list.sort,
      }),
    [serverRows, itemLabel, list.search, list.state.filters, list.sort],
  )

  /**
   * ⚠️ `policy.data` bo'lmasa `null` QOLADI, `0` ga aylantirilmaydi:
   * "chegara yo'q" degan xulosa faqat SERVER aytganda chiqadi.
   */
  const policyData = policy.isSuccess ? policy.data : null

  const columns: ColumnsType<StockApproval> = [
    {
      // Sahifalanmagan ro'yxat — raqam to'g'ridan-to'g'ri `index` dan
      key: '__rowNo',
      title: t('dataTable.rowNo'),
      width: 56,
      align: 'center',
      render: (_v, _r, index) => <Typography.Text type="secondary">{index + 1}</Typography.Text>,
    },
    {
      key: 'requestedAt',
      title: t('stock.approval.requestedAt'),
      width: 170,
      ...sortColumn(list.sort, 'requestedAt'),
      render: (_, row) => (
        <Tooltip title={utcTooltip(row.requestedAt)}>
          <span>{formatDateTime(row.requestedAt)}</span>
        </Tooltip>
      ),
    },
    {
      key: 'kind',
      title: t('stock.field.kind'),
      width: 140,
      render: (_, row) => <Tag color="gold">{t(`movementKind.${row.kind}`)}</Tag>,
    },
    {
      key: 'item',
      title: t('stock.field.item'),
      width: 200,
      render: (_, row) => {
        const label = itemLabel.get(row.itemId)
        // ⚠️ Xarita hali kelmagan yoki mol topilmagan bo'lsa — XOM `id`
        // emas va bo'sh katak ham emas. Bo'sh katak "mol yo'q" degan
        // noto'g'ri xulosa berardi (`StockMovementsPage` bilan bir xil).
        if (!label) {
          return <Typography.Text type="secondary">{t('common.loading')}</Typography.Text>
        }
        return (
          <Space direction="vertical" size={0}>
            <Typography.Text>{label.name}</Typography.Text>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {label.sku}
            </Typography.Text>
          </Space>
        )
      },
    },
    {
      key: 'warehouse',
      title: t('stock.field.warehouse'),
      width: 180,
      render: (_, row) => warehouseName.get(row.nodeId) ?? t('common.dash'),
    },
    {
      key: 'quantity',
      title: t('stock.field.quantity'),
      width: 170,
      ...sortColumn(list.sort, 'quantity'),
      render: (_, row) => {
        const unit = itemLabel.get(row.itemId)?.unit ?? ''
        return (
          <Space direction="vertical" size={0}>
            <Typography.Text strong>{`${String(row.quantity)} ${unit}`}</Typography.Text>
            {/* ⚠️ MA'NOSI TURGA QARAB BOSHQA: sanoq natijasi va chiqim
                miqdori bir xil ko'rinsa, "42" ikki xil o'qilardi. */}
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {isCountedQuantity(row.kind)
                ? t('stock.approval.countedQuantity')
                : t('stock.approval.outQuantity')}
            </Typography.Text>
          </Space>
        )
      },
    },
    {
      key: 'cost',
      title: t('stock.approval.estimatedCost'),
      width: 190,
      ...sortColumn(list.sort, 'cost'),
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          <Typography.Text>{formatMoneyValue(row.estimatedCost)}</Typography.Text>
          {/* So'rov paytidagi chegara — keyin o'zgargan bo'lsa ham
              "nega bu so'rov imzo talab qilgan" degan savol javobsiz
              qolmasin */}
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {t('stock.approval.thresholdWas', { value: formatMoneyValue(row.threshold) })}
          </Typography.Text>
        </Space>
      ),
    },
    {
      key: 'reason',
      title: t('stock.field.reason'),
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          {row.reason === null ? (
            t('common.dash')
          ) : (
            // ⚠️ «2 qator + Batafsil» — tugma matn HAQIQATAN sig'maganda
            // chiqadi, uzunlikdan taxmin qilinmaydi (`ExpandableHint`).
            <ExpandableHint text={row.reason} />
          )}
          {row.assetTag === null ? null : (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {t('stock.field.assetTagValue', { value: row.assetTag })}
            </Typography.Text>
          )}
        </Space>
      ),
    },
    {
      key: 'requestedBy',
      title: t('stock.approval.requestedBy'),
      width: 180,
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          {/* ⚠️ B4: ilgari xom `row.requestedBy` (UUID) chizilardi.
              ⚠️ `isOwnApproval` HAMON XOM qiymat bilan ishlaydi —
                 taqqoslash ko'rsatiladigan matnga ko'chirilsa
                 «meniki» belgisi nom o'zgargan kuni jimgina
                 yo'qolardi. */}
          <Typography.Text>{actorLabel(row.requestedBy, actors, t('common.dash'))}</Typography.Text>
          {isOwnApproval(row, identity) ? (
            <Tag color="blue">{t('stock.approval.you')}</Tag>
          ) : null}
        </Space>
      ),
    },
    {
      key: 'state',
      title: t('stock.approval.state'),
      width: 190,
      ...sortColumn(list.sort, 'state'),
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          <Tag color={STATE_COLOR[row.state]}>{t(`approvalState.${row.state}`)}</Tag>
          {row.decidedBy === null ? null : (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {t('stock.approval.decidedBy', {
                // ⚠️ B4: xom UUID emas, nom.
                who: actorLabel(row.decidedBy, actors, t('common.dash')),
                when: formatDateTime(row.decidedAt),
              })}
            </Typography.Text>
          )}
          {/* ⚠️ Tasdiqlangan, lekin harakat YOZILMAGAN holat — xato
              EMAS: sanoq farqi kutish paytida boshqa amal bilan
              yopilgan bo'lsa, yoziladigan narsa qolmaydi
              (`StockApproval.movementId` javadoc'i). Buni aytmaslik
              "tasdiqladim, jurnalda yo'q" degan savolni tug'dirardi. */}
          {row.state === 'APPROVED' && row.movementId === null ? (
            <Typography.Text type="warning" style={{ fontSize: 12 }}>
              {t('stock.approval.approvedNoMovement')}
            </Typography.Text>
          ) : null}
          {row.decisionNote === null ? null : (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {row.decisionNote}
            </Typography.Text>
          )}
        </Space>
      ),
    },
    {
      key: 'actions',
      title: '',
      width: 56,
      align: 'center',
      fixed: 'right',
      render: (_, row) => {
        // ⚠️ Har amal O'Z shartidan chiqadi va shartlar SERVER
        // qoidalarining nusxasi (`approvalBlock`). Ro'yxatni bu yerda
        // qayta yozish ikkinchi haqiqat manbai bo'lardi.
        const approve = approvalBlock('APPROVE', row, identity, canAdjust)
        const reject = approvalBlock('REJECT', row, identity, canAdjust)
        const cancel = approvalBlock('CANCEL', row, identity, canAdjust)

        return (
          <RowActions
            items={[
              {
                key: 'APPROVE',
                label: t('stock.approval.action.APPROVE.short'),
                onClick: () => setPending({ action: 'APPROVE', approval: row }),
                hidden: approve !== 'OK',
              },
              {
                /* ⚠️ "Tasdiqlash" amalining YO'QLIGI sababsiz qolmaydi.
                   O'z so'rovini o'zi tasdiqlash — butun mexanizmning
                   ma'nosi shu tekshiruvda, shuning uchun u yashirin
                   emas, menyuda YOZILGAN holat (bosilmaydi). */
                key: 'selfHint',
                label: t('stock.approval.selfHint'),
                onClick: () => undefined,
                disabled: true,
                hidden: approve !== 'SELF',
              },
              {
                key: 'REJECT',
                label: t('stock.approval.action.REJECT.short'),
                onClick: () => setPending({ action: 'REJECT', approval: row }),
                danger: true,
                hidden: reject !== 'OK',
              },
              {
                key: 'CANCEL',
                label: t('stock.approval.action.CANCEL.short'),
                onClick: () => setPending({ action: 'CANCEL', approval: row }),
                divider: true,
                hidden: cancel !== 'OK',
              },
            ]}
          />
        )
      },
    },
  ]

  return (
    <>
      <PageHeader title={t('nav.stockApprovals')} description={t('pageIntro.stockApprovals')} />

      {/*
        ⚠️ HUQUQ YETMAGANDA BO'SH EKRAN EMAS, SABAB. Uch blok, uch
        huquq — har biri o'zini o'zi tushuntiradi va KOD nomini ham
        yozadi: administrator uni rol konstruktorida aynan shu nom
        bilan qidiradi.
      */}
      {canRead ? null : (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('stock.approval.noReadTitle')}
          description={t('stock.approval.noReadHint')}
        />
      )}

      {canRead && !canAdjust ? (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('stock.approval.noDecideTitle')}
          description={t('stock.approval.noDecideHint')}
        />
      ) : null}

      {done === null ? null : done.action === 'APPROVE' ? (
        // ⚠️ Tasdiqlash natijasi ODDIY AMAL natijasi bilan bir xil
        // ko'rsatiladi: unda jurnal qatorlari ham, past qoldiq
        // ogohlantirishi ham bo'lishi mumkin.
        <StockOperationAlert
          operation={done.operation}
          action={actionOfKind(done.approval.kind)}
          onClose={() => setDone(null)}
        />
      ) : (
        <Alert
          type={done.action === 'REJECT' ? 'warning' : 'info'}
          showIcon
          closable
          onClose={() => setDone(null)}
          style={{ marginBottom: 16 }}
          message={t(`stock.approval.action.${done.action}.done`)}
        />
      )}

      <Card title={t('stock.policy.title')} style={{ marginBottom: 16 }}>
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('stock.policy.scopeNote')}
        />

        {policySaved === null ? null : (
          <Alert
            type="success"
            showIcon
            closable
            onClose={() => setPolicySaved(null)}
            style={{ marginBottom: 16 }}
            message={t('stock.policy.saved', {
              value: approvalRequired(policySaved)
                ? formatMoneyValue(policySaved.threshold)
                : t('stock.policy.off'),
            })}
          />
        )}

        {policy.isError ? (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
            message={translateError(policy.error, t)}
          />
        ) : null}

        <Descriptions size="small" column={1} style={{ marginBottom: 16 }}>
          <Descriptions.Item label={t('stock.policy.threshold')}>
            {policyData === null
              ? // ⚠️ "Noma'lum" va "o'chiq" ARALASHTIRILMAYDI: birinchisi
                // o'qilmadi, ikkinchisi tashkilotning qarori.
                t('common.dash')
              : approvalRequired(policyData)
                ? formatMoneyValue(policyData.threshold)
                : t('stock.policy.off')}
          </Descriptions.Item>
          <Descriptions.Item label={t('stock.policy.internalUseThreshold')}>
            {policyData === null
              ? t('common.dash')
              : policyData.internalUseThreshold.amount > 0
                ? formatMoneyValue(policyData.internalUseThreshold)
                : t('stock.policy.off')}
          </Descriptions.Item>
          <Descriptions.Item label={t('stock.policy.updatedAt')}>
            {policyData?.updatedAt
              ? `${formatDateTime(policyData.updatedAt)}${
                  /* ⚠️ N4: siyosatni KIM o'zgartirgani ham aktyor
                     identifikatori — B4 bu ustunni o'tkazib yuborgan
                     edi, chunki u jadvalda emas, `Descriptions` da. */
                  policyData.updatedBy
                    ? ` · ${actorLabel(policyData.updatedBy, actors, t('common.dash'))}`
                    : ''
                }`
              : t('stock.policy.never')}
          </Descriptions.Item>
        </Descriptions>

        {policyData !== null && !approvalRequired(policyData) ? (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message={t('stock.policy.offNote')}
          />
        ) : null}

        {canSetThreshold ? (
          <Button onClick={() => setPolicyOpen(true)}>{t('stock.policy.edit')}</Button>
        ) : (
          // ⚠️ Tugma o'rnida SABAB. "Sizda huquq yo'q" deyish yetarli
          // emas — qaysi kod kerakligi va NEGA aynan u kerakligi ham
          // yoziladi, aks holda administrator uni ombor huquqlari
          // orasidan qidirardi.
          <Alert
            type="info"
            showIcon
            message={t('stock.policy.noEditTitle')}
            description={t('stock.policy.noEditHint')}
          />
        )}
      </Card>

      {canRead ? (
        <>
          {approvals.isError ? (
            <Alert
              type="error"
              showIcon
              style={{ marginBottom: 16 }}
              message={t('dataTable.loadFailed')}
              description={translateError(approvals.error, t)}
              action={
                <Button size="small" onClick={() => void approvals.refetch()}>
                  {t('dataTable.retry')}
                </Button>
              }
            />
          ) : null}

          {/* ⚠️ So'rov YIQILGANDA "hech kim ombor ko'rmayapti" DEYILMAYDI
              — shart `isSuccess` (`guards.mjs` 3-bo'limi). */}
          {warehouses.isSuccess && warehouses.data.length === 0 ? (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message={t('stock.balance.noWarehouses')}
              description={t('stock.balance.noWarehousesHint')}
            />
          ) : null}

          <Flex wrap gap="small" align="center" style={{ marginBottom: 16 }}>
            {/* ⚠️ Matnli qidiruv MIJOZDA: serverda `q` yo'q. Qidiruv
                paneldan TASHQARIDA qoladi — u eng ko'p ishlatiladigan
                boshqaruv va uni yashirish har qidiruvga ikki bosish
                qo'shardi. */}
            <Input.Search
              allowClear
              value={list.searchText}
              onChange={(e) => list.setSearchText(e.target.value)}
              placeholder={t('stock.approval.searchPlaceholder')}
              style={{ width: 260, maxWidth: '100%' }}
            />
            {/*
              ⚠️ Uchala filtr PANELDA (egasi qarori 2026-08-20). Qidiruv
              260px, ombor 280px, holat 200px va yonida belgilagich —
              bular hech qanday ish stolida bir qatorga sig'masdi.

              ⚠️ Ombor va «faqat kutayotganlari» SERVERGA boradi. Qoralama
              tufayli ikkalasini birga o'zgartirish ikkita emas, BITTA
              so'rov yuboradi.
            */}
            <FilterDrawer<Filters>
              value={list.values}
              resetValue={{ ...list.values, node: [], state: [], pending: [] }}
              /* ⚠️ `pending` sanoqqa KIRMAYDI: u sukut bo'yicha yoqiq va
                 o'chirilganda ro'yxatni KENGAYTIRADI. Uni sanash tugmada
                 doim nishon ko'rsatib turardi. */
              activeCount={[nodeId, list.filter('state')].filter(Boolean).length}
              onApply={(next) => list.patch({ filters: next })}
            >
              {(draft, patchDraft) => {
                const draftPending = draftValue(draft, 'pending') !== 'no'
                return (
                  <>
                    <Form.Item label={t('stock.balance.allWarehouses')}>
                      <Select<string | null>
                        allowClear
                        showSearch
                        optionFilterProp="label"
                        value={draftValue(draft, 'node')}
                        onChange={(value) => patchDraft({ node: value ? [value] : [] })}
                        placeholder={t('stock.balance.allWarehouses')}
                        style={{ width: '100%' }}
                        loading={warehouses.isPending}
                        options={(warehouses.data ?? []).map((warehouse) => ({
                          value: warehouse.id,
                          label: `${warehouse.displayName} (${warehouse.code})`,
                        }))}
                      />
                    </Form.Item>
                    <Form.Item>
                      <Space size={8}>
                        <Switch
                          checked={draftPending}
                          onChange={(checked) =>
                            patchDraft({ pending: checked ? [] : ['no'] })
                          }
                        />
                        <Typography.Text>{t('stock.approval.pendingOnly')}</Typography.Text>
                      </Space>
                    </Form.Item>
                    {/*
                      ⚠️ Holat filtri FAQAT «kutayotganlari» o'chirilganda
                      chiziladi: yoqiq holatda server baribir faqat `PENDING`
                      qaytaradi va «Rad etilgan» tanlovi HAR DOIM bo'sh
                      ro'yxat berardi — operator buni ma'lumot yo'qligi deb
                      o'qirdi.
                    */}
                    {draftPending ? null : (
                      <Form.Item label={t('stock.approval.state')}>
                        <Select<string>
                          allowClear
                          value={draftValue(draft, 'state')}
                          onChange={(value) => patchDraft({ state: value ? [value] : [] })}
                          placeholder={t('stock.approval.state')}
                          style={{ width: '100%' }}
                          options={APPROVAL_STATES.map((state) => ({
                            value: state,
                            label: t(`approvalState.${state}`),
                          }))}
                        />
                      </Form.Item>
                    )}
                  </>
                )
              }}
            </FilterDrawer>
            {list.narrowed ? (
              <>
                <Typography.Text type="secondary">
                  {t('listView.shown', { shown: rows.length, total: serverRows.length })}
                </Typography.Text>
                <Button type="link" size="small" onClick={list.reset}>
                  {t('dataTable.clearFilters')}
                </Button>
              </>
            ) : null}
          </Flex>

          <Card title={t('stock.approval.listTitle', { count: serverRows.length })}>
            <Table<StockApproval>
              rowKey={(row) => row.id}
              columns={columns}
              dataSource={rows}
              loading={approvals.isPending}
              pagination={false}
              size="small"
              scroll={{ x: 1500 }}
              onChange={localSortChange<StockApproval>(list.patch)}
              locale={{
                emptyText: approvals.isError ? (
                  // ⚠️ Yiqilgan so'rov "so'rov yo'q" bo'lib ko'rinmasin
                  <EmptyState
                    variant="no-data"
                    title={t('dataTable.loadFailed')}
                    description={t('stock.approval.emptyUnknown')}
                  />
                ) : serverRows.length > 0 ? (
                  // ⚠️ Server so'rov berdi, lekin qidiruv/holat filtri
                  //    ularni ko'rsatmadi — bu «so'rov yo'q» EMAS.
                  <EmptyState
                    variant="no-data"
                    title={t('listView.noMatch')}
                    description={t('listView.noMatchHint', { total: serverRows.length })}
                    actionLabel={t('dataTable.clearFilters')}
                    onAction={list.reset}
                  />
                ) : (
                  <EmptyState
                    variant="no-data"
                    title={
                      pendingOnly ? t('stock.approval.emptyPending') : t('stock.approval.empty')
                    }
                    description={
                      policyData !== null && !approvalRequired(policyData)
                        ? t('stock.approval.emptyOffHint')
                        : t('stock.approval.emptyHint')
                    }
                  />
                ),
              }}
            />
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {t('listView.fullListNote')}
            </Typography.Text>
          </Card>
        </>
      ) : null}

      {pending === null ? null : (
        <StockApprovalDecisionModal
          key={`${pending.action}:${pending.approval.id}`}
          action={pending.action}
          approval={pending.approval}
          itemName={itemLabel.get(pending.approval.itemId)?.name ?? t('common.dash')}
          itemUnit={itemLabel.get(pending.approval.itemId)?.unit ?? ''}
          warehouseName={warehouseName.get(pending.approval.nodeId) ?? t('common.dash')}
          onClose={() => setPending(null)}
          onDone={(result) => setDone(result)}
        />
      )}

      {policyOpen ? (
        <StockPolicyModal
          policy={policyData}
          onClose={() => setPolicyOpen(false)}
          onDone={(updated) => setPolicySaved(updated)}
        />
      ) : null}
    </>
  )
}
